// Background poster generation (2026-09-28 bug batch #1).
//
// A generation used to live inside AIGeneratorSection and was ABORTED when the
// section unmounted: leaving the Home page stopped the poll, but the credit was
// already spent and the backend kept building a poster that then never reached
// the Library. The run now lives here, above the pages:
//
//   start(params)  → id    runs generationFlow.runGeneration(params) exactly as
//                          the section did (same params ⇒ same submit body);
//                          this provider only supplies the progress callbacks
//                          and the abort signal.
//   detach(id)             the page stops watching ("Continue in background",
//                          or the section unmounting). The run carries on; when
//                          it ends the user gets a toast — the poster is saved
//                          to the Library by runGeneration as always (Michael,
//                          2026-09-28: background → Library only, NOT the print
//                          queue).
//   cancel(id)             aborts, as the Cancel button always did.
//   release(id)            the watching page has consumed a finished run.
//
// Every successful save also fires `library-designs-changed` so an open
// Library reloads its list.

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { runGeneration } from '../components/home/aiGenerator/generationFlow';
import { useToast } from './ToastContext';
import { useWallet } from './WalletContext';

const BackgroundGenerationContext = createContext(null);

const TERMINAL = new Set(['done', 'rejected', 'error', 'cancelled', 'insufficient']);

export function BackgroundGenerationProvider({ children }) {
  const { t } = useTranslation('home');
  const toast = useToast();
  const wallet = useWallet();
  // id → { id, status, startedAt, title, pollState, finalising, preview, result, detached }
  const [jobs, setJobs] = useState({});
  // Synchronous per-run facts (detached / finished result). React state
  // updates are async, so a run that ends the instant it is detached must not
  // read a stale `detached` — this ref is the source of truth for that.
  const meta = useRef({});
  const aborts = useRef({});
  const running = useRef(0);

  const patch = useCallback((id, p) => {
    setJobs((cur) => (cur[id] ? { ...cur, [id]: { ...cur[id], ...p } } : cur));
  }, []);

  const drop = useCallback((id) => {
    delete meta.current[id];
    setJobs((cur) => {
      if (!cur[id]) return cur;
      const next = { ...cur };
      delete next[id];
      return next;
    });
  }, []);

  // The finished run's toast, for runs nobody is watching any more.
  const announce = useCallback((job, result) => {
    const title = result?.poster?.title || job.title || t('generator.background.untitled');
    if (result.status === 'done') {
      toast.showSuccess(t('generator.background.saved', { title }), 6000);
    } else if (result.status === 'rejected') {
      toast.showInfo(t('generator.background.rejected', { title }));
      if (result.creditsRefunded > 0) toast.showInfo(t('generator.refundedToast', { count: result.creditsRefunded }));
    } else if (result.status === 'insufficient') {
      toast.showError(t('generator.background.insufficient'));
    } else if (result.status === 'error') {
      toast.showError(result.error || t('generator.generationFailed'));
    }
    // 'cancelled' — the user asked for it; nothing to say.
  }, [t, toast]);

  const start = useCallback((params) => {
    const id = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const ctrl = new AbortController();
    aborts.current[id] = ctrl;
    const job = {
      id, status: 'running', startedAt: Date.now(), title: params.title || null,
      pollState: null, finalising: false, preview: null, result: null, detached: false,
    };
    meta.current[id] = { detached: false, result: null, title: job.title };
    setJobs((cur) => ({ ...cur, [id]: job }));

    running.current += 1;
    wallet.markPendingGeneration?.();

    (async () => {
      let result;
      try {
        result = await runGeneration({
          ...params,
          onPhase: (p) => { if (p === 'saving') patch(id, { finalising: true }); },
          onPreview: (pv) => patch(id, { preview: pv }),
          onPoll: (ps) => patch(id, { pollState: ps }),
          abortSignal: ctrl.signal,
        });
      } catch (e) {
        console.error('[bgGen] run threw', e);
        result = { status: 'error', error: t('generator.generationFailed') };
      }
      delete aborts.current[id];
      running.current = Math.max(0, running.current - 1);
      if (running.current === 0) wallet.clearPendingGeneration?.();
      if (result?.status === 'done') {
        window.dispatchEvent(new CustomEvent('library-designs-changed', { detail: { design_uuid: result.design?.design_uuid } }));
      }
      const m = meta.current[id];
      if (!m) return;                      // released meanwhile
      if (m.detached) {
        announce(m, result);
        drop(id);
      } else {
        m.result = result;
        patch(id, { status: TERMINAL.has(result?.status) ? result.status : 'error', result });
      }
    })();
    return id;
  }, [announce, drop, patch, t, wallet]);

  const detach = useCallback((id) => {
    const m = meta.current[id];
    if (!m) return;
    if (m.result) {
      // Already finished while the page watched but before it consumed the
      // result: announce now so the outcome is never lost.
      announce(m, m.result);
      drop(id);
      return;
    }
    m.detached = true;
    patch(id, { detached: true });
  }, [announce, drop, patch]);

  const cancel = useCallback((id) => { aborts.current[id]?.abort(); }, []);
  const release = useCallback((id) => { drop(id); }, [drop]);

  const backgroundCount = Object.values(jobs).filter((j) => j.detached && j.status === 'running').length;

  const value = useMemo(() => ({
    jobs, start, detach, cancel, release, backgroundCount,
  }), [jobs, start, detach, cancel, release, backgroundCount]);

  return <BackgroundGenerationContext.Provider value={value}>{children}</BackgroundGenerationContext.Provider>;
}

export function useBackgroundGeneration() {
  const ctx = useContext(BackgroundGenerationContext);
  if (!ctx) throw new Error('useBackgroundGeneration must be used within BackgroundGenerationProvider');
  return ctx;
}
